import { useEffect, useMemo } from "react";
import EggVisual from "@/components/EggVisual";

/**
 * OmniCelebrationOverlay — full-screen 🌈 celebration when a 1-in-a-million
 * SSS Omni Rainbow egg hatches.
 *
 * Props:
 *   open        — render the overlay when truthy
 *   hatched     — backend hatch result { rarity, letter_grade, tier_name,
 *                  theme, nests, bonus_items, colored_feather_color }
 *   onClose     — fired when the user dismisses (also auto-closes after 8s)
 */
export default function OmniCelebrationOverlay({ open, hatched, onClose }) {
  // Auto-dismiss after 8 seconds — enough to scream, screenshot, share
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => onClose?.(), 8000);
    return () => clearTimeout(t);
  }, [open, onClose]);

  // Precompute 36 confetti pieces with stable random positions
  const confetti = useMemo(() => {
    const colors = ["#FF0080", "#FFD700", "#00BFFF", "#8A2BE2", "#FF8C00", "#00FF7F", "#FF1493", "#22C55E"];
    return Array.from({ length: 36 }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 1.2,
      duration: 3.0 + Math.random() * 1.6,
      color: colors[i % colors.length],
      rotate: Math.random() * 360,
    }));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !hatched) return null;

  const colorFeather = hatched.colored_feather_color;
  const bonuses = hatched.bonus_items || [];

  return (
    <div
      data-testid="omni-celebration"
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
      onClick={onClose}
    >
      {/* Animated rainbow background flash */}
      <div className="absolute inset-0 omni-celebration-bg opacity-95" />
      <div className="absolute inset-0 bg-black/30" />

      {/* Confetti rain */}
      <div className="absolute inset-0 pointer-events-none">
        {confetti.map((c) => (
          <div
            key={c.id}
            className="confetti-piece"
            style={{
              left: `${c.left}%`,
              background: c.color,
              animationDelay: `${c.delay}s`,
              animationDuration: `${c.duration}s`,
              transform: `rotate(${c.rotate}deg)`,
            }}
          />
        ))}
      </div>

      {/* Hero card */}
      <div
        data-testid="omni-celebration-card"
        className="omni-flash-in relative bg-black/85 border-4 border-white px-10 py-8 max-w-lg mx-4 text-center"
        style={{
          boxShadow:
            "0 0 60px rgba(255, 0, 128, 0.7), 0 0 120px rgba(0, 191, 255, 0.5)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-white mb-3">
          🌈 1 in a Million 🐦‍🔥
        </div>
        <h2
          className="omni-title-shimmer font-display text-4xl md:text-6xl leading-[0.9] text-white"
          style={{
            background: "linear-gradient(90deg,#FF0080,#FFD700,#00BFFF,#8A2BE2)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          OMNI RAINBOW
        </h2>
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/80 mt-1 mb-5">
          SSS · Hatched
        </div>

        {/* The egg itself, but BIG */}
        <div className="flex justify-center mb-4">
          <EggVisual
            theme={hatched.theme}
            letterGrade={hatched.letter_grade || "SSS"}
            size={132}
            showRank
          />
        </div>

        {/* Rewards line */}
        <div className="text-white font-mono tabular-nums text-2xl mb-2">
          +{hatched.nests} 🪹
        </div>
        <div className="flex justify-center gap-3 text-xl mb-1">
          {bonuses.includes("phoenix_feather") && <span title="Phoenix Feather">🪶</span>}
          {bonuses.filter((b) => b === "phoenix_feather").length > 1 && (
            <span className="text-white text-sm">×{bonuses.filter((b) => b === "phoenix_feather").length}</span>
          )}
          {bonuses.includes("phoenix_tear") && <span title="Phoenix Tear">💧</span>}
          {bonuses.filter((b) => b === "phoenix_tear").length > 1 && (
            <span className="text-white text-sm">×{bonuses.filter((b) => b === "phoenix_tear").length}</span>
          )}
          {bonuses.includes("phoenix_crown") && <span title="Phoenix Crown">👑</span>}
          {hatched.heart_dropped && <span title="Phoenix Heart">🫀</span>}
        </div>
        {colorFeather && (
          <div
            className="inline-block text-[11px] uppercase tracking-widest text-white px-3 py-1 mt-3 border border-white/30"
            style={{
              background: `linear-gradient(135deg, #1F0F03, ${colorFeather})`,
            }}
          >
            🪶 +1 {colorFeather} Phoenix Feather
          </div>
        )}

        <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 mt-6">
          Your friends will see this on the feed 🌈
        </div>
        <button
          data-testid="omni-celebration-close"
          onClick={onClose}
          className="mt-5 px-6 py-2 bg-white text-black font-bold uppercase tracking-widest text-xs hover:bg-yellow-300 transition-colors"
        >
          Fly Higher 🦅
        </button>
      </div>
    </div>
  );
}
